/**
 * ComplexityGame: the "how big is my project, really?" centrepiece of the
 * NetSuite pricing page. It opens on a three-way start: only "choosing
 * NetSuite" plays the game; "already on NetSuite" and "project in trouble" end
 * on a short screen with a call to action. The game's steps build a
 * configuration; a meter climbs and cost lines switch on as choices are made;
 * a readiness step rates the reader's own side. The result shows a complexity
 * level, a core-build timeline band with its source, one lane per integration,
 * the cost lines with "not in this estimate" rows (including the reader's own
 * team's time at JobsDB medians), a readiness score, PS Global's phase split
 * and a placeholder for PS Global's fee band, all with no gate; below it the
 * reader can email themselves a scoping checklist. Users from the tier
 * checker pre-fill the people step (never over a reader's own answer).
 *
 * It never computes a NetSuite subscription figure and never shows a price for
 * an Oracle licence or module. Scoring is provisional pending PS Global's
 * calibration (see complexity-game.json). Strings live in ./complexity-game/strings.ts.
 */
'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { sendGTMEvent } from '@next/third-parties/google';
import { POSTHOG_EVENTS, isInternalTraffic } from '@/lib/posthog';
import { onHandoff, readHandoff, type PricingHandoff } from './shared/pricing-handoff';
import {
  INTEGRATIONS_STEP,
  LEVELS,
  METER_MAX,
  READINESS_STEP,
  SITUATION_INPUT,
  START_STEP,
  STEP_IDS,
  USERS_INPUT,
  branchOf,
  defaultAnswers,
  inputsFor,
  scoreAnswers,
  stepAnswerSummary,
  stepData,
  stepQuestion,
  usersOptionFor,
  type Answers,
  type GameInput,
  type GameOption,
  type Score,
} from './complexity-game/model';
import { STR } from './complexity-game/strings';
import { CSS, STYLE_ID } from './complexity-game/styles';
import IntegrationsPicker from './complexity-game/integrations-picker';
import { BranchScreen, ResultScreen } from './complexity-game/result';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';
// v2: the start step moved first and the entity question changed shape, so v1 saves are dropped.
const STORAGE_KEY = 'psg-complexity-game-v2';
const DEV = process.env.NODE_ENV !== 'production';
const RESULT_INDEX = STEP_IDS.length;

/* ------------------------------- Hooks --------------------------------- */
function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS;
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID;
      s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

type Props = Record<string, string | number | boolean>;

/** PostHog + GTM, each wrapped so a missing provider or blocked script never throws. */
function useTrack(): (event: string, props: Props) => void {
  const posthog = usePostHog();
  return useCallback(
    (event: string, props: Props) => {
      const payload = { calculator_type: 'complexity_game', timestamp: new Date().toISOString(), ...props };
      try {
        if (posthog && !isInternalTraffic()) posthog.capture(event, payload);
      } catch {
        /* analytics must never break the game */
      }
      try {
        sendGTMEvent({ event, ...payload });
      } catch {
        /* same */
      }
    },
    [posthog]
  );
}

/** `touched` lists the inputs the reader chose themselves; a hand-off never overwrites them. */
interface Saved { answers: Answers; stepIndex: number; touched?: string[] }

function loadSaved(): Saved | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Saved>;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.answers !== 'object' || parsed.answers === null) return null;
    const stepIndex = typeof parsed.stepIndex === 'number' ? Math.min(Math.max(0, parsed.stepIndex), RESULT_INDEX) : 0;
    const touched = Array.isArray(parsed.touched) ? parsed.touched.filter((t): t is string => typeof t === 'string') : [];
    return { answers: { ...defaultAnswers(), ...parsed.answers }, stepIndex, touched };
  } catch {
    return null;
  }
}

function save(data: Saved): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* private mode or quota: progress just is not kept */
  }
}

function clearSaved(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/* ------------------------------- Meter --------------------------------- */
function Meter({ score, reduced }: { score: Score; reduced: boolean }) {
  const pct = Math.min(score.points, METER_MAX) / METER_MAX;
  const bands = LEVELS.map((l, i) => {
    const next = LEVELS[i + 1]?.min ?? METER_MAX;
    return { level: l, width: ((next - l.min) / METER_MAX) * 100 };
  });
  return (
    <div className="meter" aria-live="polite">
      <div className="meter-head">
        <h4>{STR.meter.title}</h4>
        {DEV && <span className="meter-pts">{STR.meter.points(score.points)}</span>}
      </div>
      <div className="meter-track" role="meter" aria-valuemin={0} aria-valuemax={METER_MAX} aria-valuenow={Math.min(score.points, METER_MAX)} aria-valuetext={STR.meter.level(score.level.level, score.level.name)}>
        {bands.map((b) => (
          <div className="meter-band" key={b.level.level} style={{ width: `${b.width}%` }} />
        ))}
        <motion.div
          className="meter-fill"
          initial={false}
          animate={{ width: `${pct * 100}%` }}
          transition={reduced ? { duration: 0 } : { type: 'spring', stiffness: 120, damping: 20 }}
        />
      </div>
      <div className="meter-ticks" aria-hidden="true">
        {bands.map((b) => (
          <span className={`meter-tick${b.level.level === score.level.level && score.branch === 'choosing' ? ' cur' : ''}`} key={b.level.level} style={{ width: `${b.width}%` }}>
            {b.level.level}
          </span>
        ))}
      </div>
      {score.branch !== 'choosing' ? (
        <>
          <p className="meter-level">{score.branch === 'rescue' ? STR.meter.rescue : STR.meter.live}</p>
          <p className="meter-hint">{score.branch === 'rescue' ? STR.meter.rescueHint : STR.meter.liveHint}</p>
        </>
      ) : (
        <p className="meter-level">{STR.meter.level(score.level.level, score.level.name)}</p>
      )}
    </div>
  );
}

function CostLines({ lines, compact, reduced }: { lines: string[]; compact: boolean; reduced: boolean }) {
  const [open, setOpen] = useState(false);
  const show = !compact || open;
  return (
    <div className="cost">
      <div className="cost-head">
        <h4>{STR.costLines.title}</h4>
        {compact ? (
          <button type="button" className="btn-link" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {STR.costLines.count(lines.length)} {open ? STR.costLines.hide : STR.costLines.show}
          </button>
        ) : (
          <span className="cost-count">{STR.costLines.count(lines.length)}</span>
        )}
      </div>
      {show && lines.length === 0 && <p className="cost-empty">{STR.costLines.empty}</p>}
      {show && lines.length > 0 && (
        <ul className="cost-list">
          <AnimatePresence initial={false}>
            {lines.map((l) => (
              <motion.li
                className="cost-item"
                key={l}
                initial={reduced ? false : { opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduced ? undefined : { opacity: 0, x: -6 }}
                transition={{ duration: 0.18 }}
              >
                {l}
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      )}
    </div>
  );
}

/* ------------------------------- Inputs -------------------------------- */
function anchorHint(o: GameOption): React.ReactNode {
  const a = o.published_anchor;
  if (!a || a.display) return null;
  if (a.type === 'oracle') {
    return (
      <span className="opt-anchor">
        {a.text}. <span className="by">{a.source}</span>
      </span>
    );
  }
  if (a.type !== 'psg_own') return null;
  return (
    <span className="opt-anchor">
      {a.text} <span className="by">{STR.publishedBy}</span>
    </span>
  );
}

function RatingBlock({ input, value, onChange }: { input: GameInput; value: string[]; onChange: (ids: string[]) => void }) {
  const label = input.label ?? STR.inputLabels[input.id] ?? input.id;
  const labelId = `cxg-${input.id}-label`;
  const cur = value[0];
  return (
    <div className="rating">
      <div className="rating-label" id={labelId}>{label}</div>
      <div className="rating-row">
        <span className="rating-ends" aria-hidden="true">{STR.rating.low}</span>
        <div className="rating-btns" role="radiogroup" aria-labelledby={labelId}>
          {[1, 2, 3, 4, 5].map((n) => {
            const on = cur === String(n);
            return (
              <button type="button" key={n} role="radio" aria-checked={on} aria-label={STR.rating.value(label, n)} className={`rate${on ? ' on' : ''}`} onClick={() => onChange([String(n)])}>
                {n}
              </button>
            );
          })}
        </div>
        <span className="rating-ends" aria-hidden="true">{STR.rating.high}</span>
      </div>
    </div>
  );
}

function InputBlock({ input, stepId, value, onChange, prefilled = false }: { input: GameInput; stepId: string; value: string[]; onChange: (ids: string[]) => void; prefilled?: boolean }) {
  if (input.type === 'rating') return <RatingBlock input={input} value={value} onChange={onChange} />;
  const label = input.label ?? STR.inputLabels[input.id] ?? STR.stepTitles[stepId];
  const labelId = `cxg-${input.id}-label`;

  if (input.type === 'toggle') {
    const yes = input.options.find((o) => o.id === 'yes') ?? input.options[input.options.length - 1];
    const no = input.options.find((o) => o.id === 'no') ?? input.options[0];
    const on = value.includes(yes.id);
    return (
      <div className="toggle-row">
        <span className="opt-label" id={labelId}>
          {label}
          {yes.hk_specific && <span className="tag">{STR.hkTag}</span>}
        </span>
        <button type="button" role="switch" aria-checked={on} aria-labelledby={labelId} className={`switch${on ? ' on' : ''}`} onClick={() => onChange([on ? no.id : yes.id])}>
          <span className="track"><span className="knob" /></span>
          <span>{on ? STR.toggleOn : STR.toggleOff}</span>
        </button>
      </div>
    );
  }

  const multi = input.type === 'multi';
  return (
    <div className="input">
      <div className="input-label" id={labelId}>
        {label}
        {multi && <span className="hint">{STR.multiHint}</span>}
      </div>
      <div className="opts" role={multi ? 'group' : 'radiogroup'} aria-labelledby={labelId}>
        {input.options.map((o) => {
          const on = value.includes(o.id);
          const pick = (): void => {
            if (multi) onChange(on ? value.filter((v) => v !== o.id) : [...value, o.id]);
            else onChange([o.id]);
          };
          return (
            <button type="button" key={o.id} role={multi ? 'checkbox' : 'radio'} aria-checked={on} className={`opt${on ? ' on' : ''}`} onClick={pick}>
              <span className="opt-label">
                {o.label}
                {o.hk_specific && <span className="tag">{STR.hkTag}</span>}
              </span>
              {anchorHint(o)}
            </button>
          );
        })}
      </div>
      {prefilled && <p className="prefilled">{STR.prefilled}</p>}
    </div>
  );
}

function StepNote({ stepId }: { stepId: string }) {
  const s = stepData(stepId);
  const a = s?.anchor;
  if (!a || a.type !== 'psg_own') return null;
  return (
    <p className="step-note">
      {a.text}. <span className="by">{a.source ?? STR.publishedBy}</span>
    </p>
  );
}

/* ------------------------------ Component ------------------------------ */
export interface ComplexityGameProps {
  compact?: boolean;
}

export default function ComplexityGame({ compact: compactProp = false }: ComplexityGameProps) {
  useInjectedStyles();
  const reduced = useReducedMotion() ?? false;
  const track = useTrack();
  const rootRef = useRef<HTMLDivElement>(null);
  const questionRef = useRef<HTMLHeadingElement>(null);
  const [narrow, setNarrow] = useState(false);
  const compact = compactProp || narrow;

  const [answers, setAnswers] = useState<Answers>(() => defaultAnswers());
  const [stepIndex, setStepIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [hydrated, setHydrated] = useState(false);
  const [touched, setTouched] = useState<string[]>([]);
  const [prefilled, setPrefilled] = useState<string[]>([]);
  const [handoff, setHandoff] = useState<PricingHandoff>({});
  const touchedRef = useRef<string[]>([]);
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const viewedRef = useRef<Set<string>>(new Set());
  const answeredRef = useRef<Set<string>>(new Set());

  // Measure the container so the layout follows the embed width, not the viewport.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = (): void => setNarrow(el.offsetWidth < 720);
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);

  // Restore progress for this tab (deferred a tick so the server-rendered
  // defaults hydrate cleanly first), then keep it saved.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const saved = loadSaved();
      if (saved) {
        setAnswers(saved.answers);
        setStepIndex(saved.stepIndex);
        setTouched(saved.touched ?? []);
        touchedRef.current = saved.touched ?? [];
        if (saved.stepIndex > 0) startedRef.current = true;
        if (saved.stepIndex >= RESULT_INDEX) completedRef.current = true;
      }
      setHydrated(true);
    }, 0);
    return () => window.clearTimeout(t);
  }, []);
  useEffect(() => {
    if (hydrated) save({ answers, stepIndex, touched });
  }, [answers, stepIndex, touched, hydrated]);

  // Pre-fill from the tier checker: on mount (after restore) and whenever it
  // publishes again. Only inputs the reader has not chosen themselves change.
  useEffect(() => {
    if (!hydrated) return;
    const apply = (h: PricingHandoff): void => {
      setHandoff(h);
      if (typeof h.users === 'number' && h.users > 0 && !touchedRef.current.includes(USERS_INPUT)) {
        const opt = usersOptionFor(h.users);
        setAnswers((a) => ((a[USERS_INPUT] ?? [])[0] === opt ? a : { ...a, [USERS_INPUT]: [opt] }));
        setPrefilled((p) => (p.includes(USERS_INPUT) ? p : [...p, USERS_INPUT]));
      }
    };
    apply(readHandoff());
    return onHandoff(apply);
  }, [hydrated]);

  const score = useMemo(() => scoreAnswers(answers), [answers]);
  const branch = branchOf(answers);
  const branched = branch !== 'choosing';
  const atResult = stepIndex >= RESULT_INDEX;
  const stepId = atResult ? null : STEP_IDS[stepIndex];
  const extras = useMemo(
    () => ({
      users: typeof handoff.users === 'number' && prefilled.includes(USERS_INPUT) && !touched.includes(USERS_INPUT) ? handoff.users : undefined,
      linesPerMonth: typeof handoff.linesPerMonth === 'number' && handoff.linesPerMonth > 0 ? handoff.linesPerMonth : undefined,
    }),
    [handoff, prefilled, touched]
  );

  // One event per step viewed (once per step per visit), so drop-off is visible.
  useEffect(() => {
    if (!hydrated) return;
    const id = atResult ? (branched ? `branch_${branch}` : 'result') : (stepId as string);
    if (viewedRef.current.has(id)) return;
    viewedRef.current.add(id);
    track(POSTHOG_EVENTS.CALCULATOR_STEP_VIEWED, {
      step_id: id,
      step_number: atResult ? RESULT_INDEX + 1 : stepIndex + 1,
      branch,
    });
  }, [hydrated, atResult, branched, branch, stepId, stepIndex, track]);

  // Move focus to the new question so keyboard and screen-reader users follow the step.
  useEffect(() => {
    if (!hydrated || atResult) return;
    const t = window.setTimeout(() => questionRef.current?.focus({ preventScroll: true }), 60);
    return () => window.clearTimeout(t);
  }, [stepIndex, hydrated, atResult]);

  useEffect(() => {
    if (!atResult || completedRef.current) return;
    completedRef.current = true;
    track(POSTHOG_EVENTS.CALCULATOR_COMPLETED, {
      level: branched ? 0 : score.level.level,
      level_name: branched ? branch : score.level.name,
      branch,
      points: score.points,
      is_rescue: score.isRescue,
      integrations: score.integrations.length,
      cost_lines: score.costLines.length,
    });
  }, [atResult, score, track, branched, branch]);

  const markStarted = useCallback(
    (how: string) => {
      if (startedRef.current) return;
      startedRef.current = true;
      track(POSTHOG_EVENTS.CALCULATOR_STARTED, { trigger: how });
    },
    [track]
  );

  const setAnswer = (inputId: string, ids: string[]): void => {
    markStarted('choice');
    setAnswers((a) => ({ ...a, [inputId]: ids }));
    if (!touchedRef.current.includes(inputId)) {
      touchedRef.current = [...touchedRef.current, inputId];
      setTouched(touchedRef.current);
    }
  };

  const goNext = (): void => {
    markStarted('next');
    if (stepId && !answeredRef.current.has(stepId)) {
      answeredRef.current.add(stepId);
      track(POSTHOG_EVENTS.CALCULATOR_STEP_ANSWERED, {
        step_id: stepId,
        step_number: stepIndex + 1,
        branch,
        answer: stepAnswerSummary(stepId, answers),
      });
    }
    setDir(1);
    if (stepId === START_STEP && branched) setStepIndex(RESULT_INDEX);
    else setStepIndex((i) => Math.min(i + 1, RESULT_INDEX));
  };
  const goBack = (): void => {
    setDir(-1);
    if (atResult && branched) setStepIndex(STEP_IDS.indexOf(START_STEP));
    else setStepIndex((i) => Math.max(i - 1, 0));
  };
  const restart = (): void => {
    setDir(-1);
    setAnswers(defaultAnswers());
    setStepIndex(0);
    setTouched([]);
    touchedRef.current = [];
    setPrefilled([]);
    completedRef.current = false;
    startedRef.current = false;
    clearSaved();
  };
  /** From a branch screen: switch the start to "choosing" and play the game. */
  const playGame = (): void => {
    setAnswer(SITUATION_INPUT, ['choosing']);
    completedRef.current = false;
    setDir(1);
    setStepIndex(STEP_IDS.indexOf(START_STEP) + 1);
  };
  const resultTrack = useCallback(
    (kind: string, props: Record<string, string | number | boolean>) => {
      if (kind === 'checklist_sent') track(POSTHOG_EVENTS.CALCULATOR_CHECKLIST_SENT, props);
      else track(POSTHOG_EVENTS.CTA_CLICK, { cta: kind, ...props });
    },
    [track]
  );

  const lastStep = stepIndex === RESULT_INDEX - 1 || (stepId === START_STEP && branched);
  const variants = {
    enter: (d: number) => (reduced ? { opacity: 1, x: 0 } : { opacity: 0, x: 24 * d }),
    center: { opacity: 1, x: 0 },
    exit: (d: number) => (reduced ? { opacity: 1, x: 0 } : { opacity: 0, x: -24 * d }),
  };

  return (
    <div className={`cxg-root${compact ? ' compact' : ''}`} ref={rootRef}>
      <div className="head">
        <div>
          <p className="kicker">{STR.kicker}</p>
          <h2 className="title">{STR.title}</h2>
          <p className="sub">{STR.subtitle}</p>
          {DEV && <span className="dev-badge">{STR.provisional}</span>}
        </div>
        <img className="cxg-logo" src={PSGLOBAL_LOGO} alt={STR.logoAlt} />
      </div>

      <div className={`body${atResult ? ' at-result' : ''}`}>
        <div className="main">
          <AnimatePresence mode="wait" custom={dir} initial={false}>
            {atResult ? (
              <motion.div key="result" className="screen" custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.22 }}>
                {branched ? (
                  <BranchScreen
                    branch={branch as 'live' | 'rescue'}
                    answers={answers}
                    onBack={goBack}
                    onPlay={playGame}
                    onCta={(href) => resultTrack(branch === 'rescue' ? 'rescue_contact' : 'managed_services', { href })}
                  />
                ) : (
                  <ResultScreen answers={answers} score={score} extras={extras} onRestart={restart} onBack={goBack} onTrack={resultTrack} />
                )}
              </motion.div>
            ) : (
              <motion.div key={stepId} className="screen" custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.22 }}>
                <div className="stepbar">
                  <div className="dots" aria-hidden="true">
                    {STEP_IDS.map((s, i) => (
                      <span className={`dot${i < stepIndex ? ' done' : i === stepIndex ? ' active' : ''}`} key={s} />
                    ))}
                  </div>
                  <span className="stepcount">{STR.stepOf(stepIndex + 1, STEP_IDS.length)}</span>
                </div>

                <h3 className="q" ref={questionRef} tabIndex={-1}>
                  {stepQuestion(stepId as string)}
                </h3>
                {stepId === READINESS_STEP && <p className="res-sub">{STR.readiness.intro}</p>}

                {stepId === INTEGRATIONS_STEP ? (
                  <IntegrationsPicker selected={answers[INTEGRATIONS_STEP] ?? []} onChange={(slugs) => setAnswer(INTEGRATIONS_STEP, slugs)} />
                ) : (
                  <>
                    {inputsFor(stepId as string)
                      // On the start step, "what are you moving from" only matters when choosing NetSuite.
                      .filter((input) => !(stepId === START_STEP && branched && input.id !== SITUATION_INPUT))
                      .map((input) => (
                        <InputBlock
                          key={input.id}
                          input={input}
                          stepId={stepId as string}
                          value={answers[input.id] ?? []}
                          onChange={(ids) => setAnswer(input.id, ids)}
                          prefilled={prefilled.includes(input.id) && !touched.includes(input.id)}
                        />
                      ))}
                    <StepNote stepId={stepId as string} />
                  </>
                )}

                <div className="nav">
                  <button type="button" className="btn btn-ghost" onClick={goBack} disabled={stepIndex === 0}>
                    {STR.back}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={goNext}>
                    {lastStep ? STR.seeResult : STR.next}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {!atResult && (
          <aside className="side" aria-label={STR.meter.title}>
            <Meter score={score} reduced={reduced} />
            <CostLines lines={score.costLines} compact={compact} reduced={reduced} />
          </aside>
        )}
      </div>

      <div className="foot">
        <p className="src">
          {STR.footer}
          <span className="checked">{STR.lastChecked}</span>
        </p>
      </div>
    </div>
  );
}
